/*
 * ============================================================
 * SAV_ERP — Smart Account Vision Enterprise Resource Planning
 * Copyright (c) 2026 Hatem Mohamed Abdalla.
 * All rights reserved.
 *
 * This file is part of a proprietary software product owned
 * solely by Hatem Mohamed Abdalla (UAE, +971 50 390 9781).
 * Copying, redistribution, modification, reverse engineering
 * or reuse in any form without prior written consent of the
 * copyright owner is strictly prohibited and will be
 * prosecuted under UAE Federal Law No. 38/2021 on Copyright
 * and applicable international treaties.
 *
 * Fingerprint: SAV-ERP-HMA-2026-#a7f3d0
 * ============================================================
 */
import React, { useState } from 'react';
import { apiPost, apiPut } from '../services/apiClient';
import { extractErrorMessage, logError } from '../utils/errorHandler';

const CustomerModal = ({ 
  isOpen, 
  onClose, 
  customer = null, 
  onSaved, 
  t, 
  language 
}) => {
  const [formData, setFormData] = useState({
    customer_code: customer?.customer_code || '',
    customer_name: customer?.customer_name || '',
    customer_name_ar: customer?.customer_name_ar || '',
    contact_person: customer?.contact_person || '',
    email: customer?.email || '',
    phone: customer?.phone || '',
    mobile: customer?.mobile || '',
    address: customer?.address || '',
    address_ar: customer?.address_ar || '',
    city: customer?.city || '',
    country: customer?.country || 'UAE',
    tax_number: customer?.tax_number || '',
    vat_not_registered: customer?.vat_not_registered || false,
    credit_limit: customer?.credit_limit || 0,
    payment_terms: customer?.payment_terms || 30,
    notes: customer?.notes || ''
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value, type: inputType, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: inputType === 'checkbox'
        ? Boolean(checked)
        : name === 'credit_limit' || name === 'payment_terms' 
          ? parseFloat(value) || 0 
          : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (customer) {
        // Update existing customer
        await apiPut(`/customers/${customer.id}`, formData);
      } else {
        // Create new customer
        await apiPost('/customers', formData);
      }
      
      onSaved();
      onClose();
    } catch (error) {
      logError('Customer Save', error);
      setError(extractErrorMessage(error, 'فشل في حفظ العميل'));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-full overflow-y-auto">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          {customer ? t('edit') + ' ' + t('customerName') : t('addCustomer')}
        </h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('customerCode')} (🔄 توليد تلقائي - اتركه فارغاً)
              </label>
              <input
                type="text"
                name="customer_code"
                value={formData.customer_code}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-green-300 bg-green-50 rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500"
                placeholder="سيتم التوليد تلقائياً: CUST000001"
                disabled={customer} // Don't allow editing customer code
              />
              <p className="text-xs text-green-600 mt-1">💡 اتركه فارغاً لتوليد تلقائي بالبادئة CUST</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('customerName')} (English) *
              </label>
              <input
                type="text"
                name="customer_name"
                value={formData.customer_name}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('customerName')} (العربية)
              </label>
              <input
                type="text"
                name="customer_name_ar"
                value={formData.customer_name_ar}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Contact Person
              </label>
              <input
                type="text"
                name="contact_person"
                value={formData.contact_person}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('email')}
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('phone')}
              </label>
              <input
                type="text"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Mobile
              </label>
              <input
                type="text"
                name="mobile"
                value={formData.mobile}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('address')} (English)
              </label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('address')} (العربية)
              </label>
              <input
                type="text"
                name="address_ar"
                value={formData.address_ar}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                City
              </label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Country
              </label>
              <select
                name="country"
                value={formData.country}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="UAE">UAE</option>
                <option value="Saudi Arabia">Saudi Arabia</option>
                <option value="Kuwait">Kuwait</option>
                <option value="Qatar">Qatar</option>
                <option value="Bahrain">Bahrain</option>
                <option value="Oman">Oman</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('taxNumber')} (اختياري - للشركات المسجلة ضريبياً فقط)
              </label>
              <input
                type="text"
                name="tax_number"
                value={formData.tax_number}
                onChange={handleInputChange}
                minLength="15"
                maxLength="15"
                disabled={formData.vat_not_registered}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
                placeholder="100123456789003 (اختياري)"
                data-testid="customer-tax-number-input"
              />
              <p className="text-xs text-gray-500 mt-1">💡 رقم ضريبي مكون من 15 رقم للشركات المسجلة في دولة الإمارات (اختياري)</p>
              <label className="mt-2 flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  name="vat_not_registered"
                  checked={Boolean(formData.vat_not_registered)}
                  onChange={handleInputChange}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  data-testid="customer-vat-not-registered-toggle"
                />
                <span className="text-sm text-gray-700">
                  {language === 'ar'
                    ? 'العميل غير مسجل ضريبياً (تُطبع الفاتورة بعنوان "فاتورة" بدلًا من "فاتورة ضريبية")'
                    : 'Customer is not VAT-registered (prints as "INVOICE" instead of "TAX INVOICE")'}
                </span>
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('creditLimit')} (AED)
              </label>
              <input
                type="number"
                name="credit_limit"
                value={formData.credit_limit}
                onChange={handleInputChange}
                min="0"
                step="0.01"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('paymentTerms')} ({t('days')})
              </label>
              <input
                type="number"
                name="payment_terms"
                value={formData.payment_terms}
                onChange={handleInputChange}
                min="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              {t('cancel')}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {loading ? t('loading') : (customer ? t('save') : t('create'))}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CustomerModal;